/**
 * 배관 치수 로더.
 *
 * 원본 데이터: /data/pipe-dimensions.json
 *   **KOSHA GUIDE D-63-2018 <별표2> '배관의 스케줄별 내경'** 을 옮긴 것이다.
 *   데이터 수정 시 `python data/verify-pipe-dimensions.py` 재실행 필수.
 *
 * ⚠ 이 표는 API 520/521/526 에는 없다. 국내 안전밸브 배출배관 설계가
 *   따르는 KOSHA 지침의 표를 기준으로 삼았다. **사내 배관 사양이 있으면
 *   그쪽이 우선한다** — 화면에서 내경을 직접 넣을 수 있게 해 뒀다.
 *
 * 호칭경마다 있는 스케줄이 다르다(작은 관경에는 Sch 100~160 이 없는 식).
 * 화면은 `schedulesFor()` 로 그 호칭경에 실제로 있는 것만 보여야 한다.
 *
 * 이 파일은 JSON 에 타입만 입힌다. 값을 여기서 가공하지 않는다.
 */
import raw from "../../data/pipe-dimensions.json";

export interface PipeWall {
  readonly wallThicknessMm: number;
  readonly innerDiameterMm: number;
}

export interface PipeSize {
  /** 호칭경 (inch 표기) — 예: "1-1/2", "8" */
  readonly nps: string;
  readonly outerDiameterMm: number;
  readonly walls: Readonly<Record<string, PipeWall>>;
}

export const PIPE_SCHEDULES: readonly string[] = raw.schedules;
export const PIPE_SIZES: readonly PipeSize[] = raw.sizes;
export const PIPE_SOURCE: string = raw.source;

/** 호칭경·스케줄로 내경(mm)을 찾는다. 없으면 null. */
export function innerDiameterMm(nps: string, schedule: string): number | null {
  const size = PIPE_SIZES.find((s) => s.nps === nps);
  return size?.walls[schedule]?.innerDiameterMm ?? null;
}

export function findPipeSize(nps: string): PipeSize | null {
  return PIPE_SIZES.find((s) => s.nps === nps) ?? null;
}

/**
 * 그 호칭경에 실제로 존재하는 스케줄만 표 순서대로 준다.
 * 없는 조합을 화면에 띄우면 사용자가 골라 놓고 "내경을 확인하십시오"만
 * 보게 된다.
 */
export function schedulesFor(nps: string): readonly string[] {
  const size = findPipeSize(nps);
  if (size === null) return [];
  return PIPE_SCHEDULES.filter((s) => size.walls[s] !== undefined);
}

/**
 * 고른 스케줄이 그 호칭경에 없으면 가장 가까운 것으로 바꿔 준다.
 * 호칭경을 바꿨을 때 조합이 깨지는 것을 막는다.
 */
export function nearestSchedule(nps: string, schedule: string): string {
  const available = schedulesFor(nps);
  if (available.length === 0) return schedule;
  if (available.includes(schedule)) return schedule;
  return available.includes("40") ? "40" : available[0]!;
}

/**
 * API 526 밸브 규격의 NPS(숫자)를 이 표의 호칭경 표기로 바꾼다.
 * 예: 1.5 → "1-1/2", 8 → "8"
 */
export function npsLabel(nps: number): string | null {
  const match = PIPE_SIZES.find((s) => npsValue(s.nps) === nps);
  return match?.nps ?? null;
}

/** "1-1/2" → 1.5 */
export function npsValue(nps: string): number {
  if (nps.includes("-")) {
    const [whole, frac] = nps.split("-");
    const [num, den] = frac!.split("/");
    return Number(whole) + Number(num) / Number(den);
  }
  if (nps.includes("/")) {
    const [num, den] = nps.split("/");
    return Number(num) / Number(den);
  }
  return Number(nps);
}
